import React, { useState, useEffect } from "react";
import api from "config/axiosConfig";
import LoadingSkeleton from "ui/LoadingSkeleton";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "ui/Tooltip";
import {
  isPushNotificationSupported,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from "util/pushNotifications";
import { toast } from "sonner";
import { apiRoutes } from "util/apiRoutes";

// Notification types with display names and descriptions
const NOTIFICATION_TYPES = [
  {
    group: "All",
    items: [
      {
        type: "all",
        name: "All notifications",
        description: "Globally enable or disable notifications for a channel",
      },
    ],
  },
  {
    group: "Subscriptions",
    items: [
      {
        type: "subscription_issue",
        name: "Issues",
        description: "Someone adds an influence or shares posts on issues you're subscribed to",
      },
      {
        type: "subscription_comment",
        name: "Discussions",
        description: "Comments on issues and posts you're subscribed to",
      },
    ],
  },
  {
    group: "Follows",
    items: [
      {
        type: "follow_new",
        name: "New follower",
        description: "Someone follows you",
      },
      {
        type: "follow_request",
        name: "Follow request",
        description: "Someone requests to follow you",
      },
      {
        type: "follow_accept",
        name: "Follow request accepted",
        description: "Your follow request is accepted",
      },
    ],
  },
  {
    group: "Content",
    items: [
      {
        type: "comment_post",
        name: "New comment",
        description: "Someone comments on your content",
      },
      {
        type: "comment_reply",
        name: "Comment reply",
        description: "Someone replies to your comment",
      },
      {
        type: "comment_mention",
        name: "Mention",
        description: "You're mentioned in a comment",
      },
      {
        type: "vote_up",
        name: "Prioritize",
        description: "Someone prioritized your content",
      },
      {
        type: "new_content",
        name: "New content",
        description: "Someone you follow posts new content",
      },
      {
        type: "coauthor_invite",
        name: "Collaboration",
        description: "Someone invites you to collaborate",
      },
    ],
  },
  {
    group: "Parteas",
    items: [
      {
        type: "team_join_request",
        name: "Partea join request",
        description: "Someone wants to join your Partea",
      },
      {
        type: "team_invite",
        name: "Partea invitation",
        description: "You're invited to join a Partea",
      },
      {
        type: "team_role_change",
        name: "Partea role update",
        description: "Your role in a Partea changes",
      },
      {
        type: "team_accept",
        name: "Partea request accepted",
        description: "You're accepted into a Partea",
      },
    ],
  },
  {
    group: "Chats",
    items: [
      {
        type: "chat_new_message",
        name: "New message",
        description: "Someone sends you a direct, group, or partea chat message",
      },
      {
        type: "chat_request",
        name: "Chat request",
        description: "Someone outside your default message settings wants to start a conversation",
      },
      {
        type: "chat_mention",
        name: "Chat mention",
        description: "Someone mentions you in a chat message",
      },
      {
        type: "chat_reaction",
        name: "Message reaction",
        description: "Someone reacts to one of your messages",
      },
      {
        type: "chat_thread_reply",
        name: "Thread reply",
        description: "Someone replies in a chat thread you are participating in",
      },
      {
        type: "chat_poll_vote",
        name: "Chat poll vote",
        description: "Someone votes on a poll attached to your chat message",
      },
    ],
  },
  {
    group: "Quizzes",
    items: [
      {
        type: "quiz_player_completed",
        name: "Quiz completion",
        description: "A player finishes your Societea Challenge",
      },
      {
        type: "quiz_all_completed",
        name: "Everyone finished",
        description: "All currently active quiz participants have completed the challenge",
      },
    ],
  },
  {
    group: "Product",
    items: [
      {
        type: "product_new",
        name: "Product updates",
        description: "New version releases and product announcements",
      },
    ],
  },
];

interface NotificationPreference {
  notification_type: string;
  active: boolean;
  channel?: string;
}

const UserSettingsNotifications: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [emailPreferences, setEmailPreferences] = useState<Record<string, boolean>>({});
  const [pushPreferences, setPushPreferences] = useState<Record<string, boolean>>({});
  const [savingType, setSavingType] = useState<string | null>(null);
  const [pushSupported, setPushSupported] = useState<boolean>(false);

  useEffect(() => {
    setPushSupported(isPushNotificationSupported());
  }, []);

  useEffect(() => {
    // Fetch preferences once push support state is known
    fetchNotificationPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushSupported]);

  const fetchNotificationPreferences = async () => {
    try {
      setLoading(true);
      const response = await api.get(apiRoutes.user.notificationPreferences);
      const preferences = response.data.notifications || [];
      
      // Initialize default states (Email ON by default, Push OFF by default)
      const emailPrefs: Record<string, boolean> = {};
      const pushPrefs: Record<string, boolean> = {};
      
      // Set all types default values
      NOTIFICATION_TYPES.forEach(group => {
        group.items.forEach(item => {
          emailPrefs[item.type] = true;
          pushPrefs[item.type] = false;
        });
      });
      
      // Apply saved preferences
      preferences.forEach((pref: NotificationPreference) => {
        if (pref.channel === "push" || pref.channel === undefined) {
          pushPrefs[pref.notification_type] = pref.active;
        }
        if (pref.channel === "email" || pref.channel === undefined) {
          emailPrefs[pref.notification_type] = pref.active;
        }
      });
      // If no explicit server pref for global push, infer from existing subscription
      const allPushPref = preferences.find((p: NotificationPreference) => p.notification_type === "all" && (p.channel === "push" || p.channel === undefined));
      if (!allPushPref && pushSupported && 'serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const existing = await registration.pushManager.getSubscription();
          if (existing) {
            pushPrefs["all"] = true;
          }
        } catch (_) {
          // keep default
        }
      }

      setEmailPreferences(emailPrefs);
      setPushPreferences(pushPrefs);
    } catch (error) {
      console.error("Error fetching notification preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateNotificationPreference = async (
    notificationType: string,
    active: boolean,
    channel: "email" | "push"
  ) => {
    try {
      setSavingType(notificationType + channel);
      // Handle subscription/unsubscription for global push before persisting
      if (notificationType === "all" && channel === "push") {
        const ok = await ensurePushSubscriptionForAll(active);
        if (!ok) {
          setSavingType(null);
          return;
        }
      }
      await api.put(apiRoutes.user.notificationPreferences, {
        notification_type: notificationType,
        active,
        channel
      });
      
      // Update local state
      if (channel === "email") {
        setEmailPreferences(prev => ({ ...prev, [notificationType]: active }));
      } else {
        setPushPreferences(prev => ({ ...prev, [notificationType]: active }));
      }
    } catch (error) {
      console.error("Error updating notification preference:", error);
      // Revert change on error
      if (channel === "email") {
        setEmailPreferences(prev => ({ ...prev, [notificationType]: !active }));
      } else {
        setPushPreferences(prev => ({ ...prev, [notificationType]: !active }));
      }
    } finally {
      setSavingType(null);
    }
  };

  // Ensure push subscription lifecycle for global push toggle
  const ensurePushSubscriptionForAll = async (active: boolean): Promise<boolean> => {
    if (active) {
      if (!pushSupported) {
        toast.error("Push notifications are not supported in your browser.");
        return false;
      }
      const permission = await requestNotificationPermission();
      if (permission !== "granted") {
        toast.error("Notification permission denied. Please enable notifications in your browser settings.");
        return false;
      }
      const registration = await registerServiceWorker();
      if (!registration) {
        toast.error("Failed to register service worker for push notifications.");
        return false;
      }
      const subscription = await subscribeToPushNotifications(registration);
      if (!subscription) {
        toast.error("Failed to enable push notifications.");
        return false;
      }
      toast.success("Push notifications enabled successfully!");
      return true;
    }
    const success = await unsubscribeFromPushNotifications();
    if (!success) {
      toast.error("Failed to disable push notifications. Please try again.");
      return false;
    }
    toast.success("Push notifications disabled.");
    return true;
  };

  if (loading) {
    return <LoadingSkeleton variant="settings" count={3} className="" />;
  }

  return (
    <div className="p-2">
      <div className="mb-4">
        <h2 className="text-lg font-medium text-eoe-text dark:text-eoe-dark-text">
          Notification Preferences
        </h2>
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
          Control which notifications you receive via email and in-app.
        </p>
      </div>

      {NOTIFICATION_TYPES.map((group) => (
        <div key={group.group} className="mb-6">
          <h3 className="text-md font-medium mb-2 text-eoe-text dark:text-eoe-dark-text">
            {group.group}
          </h3>
          <div className="space-y-3">
            {group.items.map((item) => (
              <div
                key={item.type}
                className="flex items-center justify-between p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md"
              >
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="font-medium text-eoe-text dark:text-eoe-dark-text">
                      {item.name}
                    </span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="ml-1 cursor-help">
                            <Info size={16} className="text-eoe-text-sub dark:text-eoe-dark-text-sub" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{item.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {item.description}
                  </p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex flex-col items-center">
                    <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub mb-1">
                      Email
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        id={`email-${item.type}`}
                        checked={emailPreferences[item.type] || false}
                        onChange={(e) =>
                          updateNotificationPreference(
                            item.type,
                            e.target.checked,
                            "email"
                          )
                        }
                        className="toggle-switch-input"
                      disabled={savingType === item.type + "email" || (item.type !== "all" && (emailPreferences["all"] === false))}
                      />
                      {savingType === item.type + "email" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover opacity-50 rounded-full">
                          <div className="animate-spin h-3 w-3 border-2 border-eoe-text-sub dark:border-eoe-dark-text-sub border-t-transparent rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub mb-1">
                      Push
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        id={`push-${item.type}`}
                        checked={pushPreferences[item.type] || false}
                        onChange={(e) =>
                          updateNotificationPreference(
                            item.type,
                            e.target.checked,
                            "push"
                          )
                        }
                        className="toggle-switch-input"
                      disabled={savingType === item.type + "push" || (item.type !== "all" && (pushPreferences["all"] === false))}
                      />
                      {savingType === item.type + "push" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover opacity-50 rounded-full">
                          <div className="animate-spin h-3 w-3 border-2 border-eoe-text-sub dark:border-eoe-dark-text-sub border-t-transparent rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default UserSettingsNotifications;
